'use client'

import * as Tooltip from '@radix-ui/react-tooltip'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { NativeSelect } from '@/components/ui/native-select'
import { Toggle } from '@/components/ui/toggle'
import {
  DEFAULT_SYNC_REPO_NAME,
  type GitHubSetupTargetMode,
} from '@/lib/github-sync-shared'
import { trpc } from '@/lib/trpc'
import { SettingRow } from './setting-row'

type SyncMode = 'accepted_only' | 'all_submissions'
type InitialBackfillMode = SyncMode | 'none'
type RepoSelectionOption = {
  ownerLogin: string
  name: string
  id?: number
}

const SYNC_MODE_OPTIONS = [
  { value: 'accepted_only', label: 'Accepted only' },
  { value: 'all_submissions', label: 'All submissions' },
]

const TARGET_MODE_OPTIONS: Array<{
  value: GitHubSetupTargetMode
  label: string
}> = [
  { value: 'create_new', label: 'Create a new BeyondCTCI repository' },
  {
    value: 'use_existing_bctci',
    label: 'Reconnect an existing BeyondCTCI repository',
  },
  {
    value: 'use_existing_external',
    label: 'Sync into an existing non-BeyondCTCI repository',
  },
]

const INITIAL_BACKFILL_CARDS: Array<{
  value: InitialBackfillMode
  title: string
  description: React.ReactNode
}> = [
  {
    value: 'accepted_only',
    title: 'Sync fully-passing submissions',
    description: (
      <>
        Sync history for all available previously attempted problems where code
        submitted fully passed all test cases.{' '}
        <span className="underline decoration-1 underline-offset-2">
          Excludes submitted code that did not pass all test cases.
        </span>
      </>
    ),
  },
  {
    value: 'all_submissions',
    title: 'Sync all submissions',
    description: (
      <>
        Sync history for all available previously attempted problems.{' '}
        <span className="underline decoration-1 underline-offset-2">
          Includes submissions where code did not fully pass all test cases.
        </span>
      </>
    ),
  },
  {
    value: 'none',
    title: 'No history sync',
    description:
      'Connect the repository now but do not port over any previous history. Syncing history moving forward will still be possible and choosing to restore all history will be possible as well.',
  },
]

const MIN_BULK_SYNC_INDICATOR_MS = 3000

function SettingsSection({
  title,
  titleClassName,
  danger = false,
  children,
}: {
  title: string
  titleClassName?: string
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`space-y-1 border px-3 py-2 ${
        danger
          ? 'border-destructive/60 rounded-none bg-muted/20'
          : 'border-border/70 rounded-xl bg-muted/20'
      }`}
    >
      <h3
        className={`pb-2 text-xs font-semibold uppercase tracking-wider ${
          titleClassName ?? 'text-muted-foreground'
        }`}
      >
        {title}
      </h3>
      <div className="divide-border/70 divide-y">{children}</div>
    </div>
  )
}

function SelectionCard({
  title,
  description,
  selected,
  onClick,
}: {
  title: string
  description: React.ReactNode
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/60'
      }`}
    >
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-muted-foreground mt-1 text-xs">{description}</p>
      </div>
      <span
        aria-hidden
        className={`mt-3 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
          selected ? 'border-primary' : 'border-muted-foreground/50'
        }`}
      >
        <span
          className={`block h-1.5 w-1.5 rounded-full ${
            selected ? 'bg-primary' : 'bg-transparent'
          }`}
        />
      </span>
    </button>
  )
}

function PermissionScopeTooltip({
  scope,
  description,
}: {
  scope: string
  description: string
}) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          className="cursor-help underline decoration-dotted outline-none"
        >
          {scope}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          align="center"
          className="border-border bg-background text-foreground z-[100001] max-w-xs rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
        >
          {description}
          <Tooltip.Arrow className="fill-background" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

function normalizeRepoKey(ownerLogin: string, repoName: string): string {
  return `${ownerLogin}::${repoName}`
}

function normalizeRepositoryName(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function toSyncMode(mode: InitialBackfillMode): SyncMode {
  return mode === 'none' ? 'accepted_only' : mode
}

function parseOauthFeedbackFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  if (params.get('github_sync_pending') === '1') {
    return 'pending'
  }
  const error = params.get('github_sync_error')
  if (error) return error
  return null
}

export function GitHubIntegrationSettings() {
  const [renameRepoName, setRenameRepoName] = useState('')
  const [bulkMode, setBulkMode] = useState<SyncMode>('accepted_only')
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [connectConfirmOpen, setConnectConfirmOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState('')
  const [targetMode, setTargetMode] =
    useState<GitHubSetupTargetMode>('create_new')
  const [selectedExistingRepoKey, setSelectedExistingRepoKey] = useState('')
  const [initialSyncMode, setInitialSyncMode] =
    useState<InitialBackfillMode>('accepted_only')
  const [editingRepoTarget, setEditingRepoTarget] = useState(false)
  const [optimisticAutoSync, setOptimisticAutoSync] = useState<boolean | null>(
    null,
  )
  const [optimisticPendingDelta, setOptimisticPendingDelta] = useState<{
    accepted_only: number
    all_submissions: number
  } | null>(null)
  const [activeBulkMode, setActiveBulkMode] = useState<SyncMode | null>(null)
  const [bulkSyncIndicatorEndsAt, setBulkSyncIndicatorEndsAt] = useState<
    number | null
  >(null)
  const [finalizeRepoName, setFinalizeRepoName] = useState(
    DEFAULT_SYNC_REPO_NAME,
  )
  const oauthFeedbackHandledRef = useRef(false)
  const previousSyncStatusRef = useRef<string | null>(null)
  const initializedConnectedEditorRef = useRef(false)

  const utils = trpc.useUtils()
  const statusQuery = trpc.github.getStatus.useQuery()
  const isPendingSetup = statusQuery.data?.pendingSetup ?? false
  const isConnected = statusQuery.data?.connected ?? false
  const ownersQuery = trpc.github.listOwners.useQuery(undefined, {
    enabled: isPendingSetup || editingRepoTarget || isConnected,
  })
  const ownerReposQuery = trpc.github.listOwnerRepositories.useQuery(
    { ownerLogin: selectedOwner },
    {
      enabled: (isPendingSetup || editingRepoTarget) && Boolean(selectedOwner),
    },
  )
  const startConnectMutation = trpc.github.startConnect.useMutation()
  const finalizeMutation = trpc.github.finalizeConnection.useMutation({
    onSuccess: async () => {
      await utils.github.getStatus.invalidate()
      toast.success('GitHub repository created — syncing your submissions')
    },
    onError: (error) => toast.error(error.message),
  })
  const updateConnectionTargetMutation =
    trpc.github.updateConnectionTarget.useMutation({
      onSuccess: async () => {
        await utils.github.getStatus.invalidate()
        setEditingRepoTarget(false)
        toast.success('GitHub repository target updated')
      },
      onError: (error) => toast.error(error.message),
    })
  const updateAutoSyncMutation = trpc.github.updateAutoSync.useMutation({
    onSuccess: async () => {
      await utils.github.getStatus.invalidate()
    },
    onError: (error) => toast.error(error.message),
  })
  const renameRepositoryMutation = trpc.github.renameRepository.useMutation({
    onSuccess: async () => {
      await utils.github.getStatus.invalidate()
      toast.success('Repository renamed')
    },
    onError: (error) => toast.error(error.message),
  })
  const triggerBulkSyncMutation = trpc.github.triggerBulkSync.useMutation({
    onSuccess: async () => {
      setActiveBulkMode(bulkMode)
      setOptimisticPendingDelta({
        accepted_only: bulkMode === 'accepted_only' ? pendingAccepted : 0,
        all_submissions: bulkMode === 'all_submissions' ? pendingAll : 0,
      })
      await utils.github.getStatus.invalidate()
      toast.success('Bulk sync queued')
    },
    onError: (error) => toast.error(error.message),
  })
  const disconnectMutation = trpc.github.disconnect.useMutation({
    onSuccess: async () => {
      await utils.github.getStatus.invalidate()
      toast.success('GitHub account disconnected')
      setSelectedOwner('')
      setSelectedExistingRepoKey('')
      setTargetMode('create_new')
      setFinalizeRepoName(DEFAULT_SYNC_REPO_NAME)
      setInitialSyncMode('accepted_only')
      setEditingRepoTarget(false)
      setOptimisticPendingDelta(null)
      setActiveBulkMode(null)
      setBulkSyncIndicatorEndsAt(null)
      initializedConnectedEditorRef.current = false
    },
    onError: (error) => toast.error(error.message),
  })

  useEffect(() => {
    if (oauthFeedbackHandledRef.current) return
    const feedback = parseOauthFeedbackFromUrl()
    if (!feedback) return
    oauthFeedbackHandledRef.current = true
    if (feedback === 'pending') {
      toast.success(
        'GitHub authorized — choose where to create your repository',
      )
    } else {
      toast.error(feedback)
    }
    if (typeof window !== 'undefined') {
      const cleaned = new URL(window.location.href)
      cleaned.searchParams.delete('github_sync_pending')
      cleaned.searchParams.delete('github_sync_error')
      window.history.replaceState({}, '', cleaned.toString())
    }
  }, [])

  const status = statusQuery.data
  const connectedStatus = status?.connected ? status : null
  const uiMode: SyncMode = connectedStatus?.autoSyncMode ?? 'accepted_only'
  const owners = ownersQuery.data?.owners ?? []
  const connectedOwnerAvatar =
    owners.find((owner) => owner.login === connectedStatus?.githubLogin)
      ?.avatarUrl ?? null
  const bctciManagedRepos = ownerReposQuery.data?.bctciManaged ?? []
  const externalRepos = ownerReposQuery.data?.external ?? []
  const existingRepoOptions =
    targetMode === 'use_existing_bctci' ? bctciManagedRepos : externalRepos
  const configReady = status?.configReady ?? true
  const pendingAccepted = connectedStatus?.pendingAcceptedCount ?? 0
  const pendingAll = connectedStatus?.pendingAllCount ?? 0
  const displayedPendingAccepted = Math.max(
    0,
    pendingAccepted - (optimisticPendingDelta?.accepted_only ?? 0),
  )
  const displayedPendingAll = Math.max(
    0,
    pendingAll - (optimisticPendingDelta?.all_submissions ?? 0),
  )
  const isConnectedStatus = connectedStatus !== null
  const isSetupEditorVisible =
    isPendingSetup || (isConnectedStatus && editingRepoTarget)
  const isSyncRunning = connectedStatus?.syncStatus === 'running'
  const connectedConnectionId = connectedStatus?.connectionId ?? null
  const connectedSyncStatus = connectedStatus?.syncStatus ?? null
  const runningMode =
    connectedSyncStatus === 'running'
      ? (connectedStatus?.bulkSyncMode ?? toSyncMode(initialSyncMode))
      : null
  const spinnerMode = activeBulkMode ?? runningMode
  const isBulkSyncIndicatorVisible =
    isSyncRunning ||
    (bulkSyncIndicatorEndsAt !== null && Date.now() < bulkSyncIndicatorEndsAt)
  const isBusy =
    startConnectMutation.isPending ||
    finalizeMutation.isPending ||
    updateConnectionTargetMutation.isPending ||
    updateAutoSyncMutation.isPending ||
    renameRepositoryMutation.isPending ||
    triggerBulkSyncMutation.isPending ||
    disconnectMutation.isPending

  // Keep status and pending counts fresh while background sync workers run.
  // Also poll during the visible syncing indicator so we don't miss queued →
  // running transitions before GitHub reflects the final pending counts.
  useEffect(() => {
    if (
      !connectedConnectionId ||
      (connectedSyncStatus !== 'running' && !isBulkSyncIndicatorVisible)
    ) {
      return
    }

    const intervalId = window.setInterval(() => {
      void statusQuery.refetch()
    }, 2500)

    return () => window.clearInterval(intervalId)
  }, [
    connectedConnectionId,
    connectedSyncStatus,
    isBulkSyncIndicatorVisible,
    statusQuery,
  ])

  // Force one final refresh when a running sync transitions to a terminal state.
  useEffect(() => {
    const currentSyncStatus = connectedStatus?.syncStatus ?? null
    const previousSyncStatus = previousSyncStatusRef.current
    previousSyncStatusRef.current = currentSyncStatus

    if (
      previousSyncStatus === 'running' &&
      currentSyncStatus !== null &&
      currentSyncStatus !== 'running'
    ) {
      void statusQuery.refetch()
    }
  }, [connectedStatus?.syncStatus, statusQuery])

  useEffect(() => {
    if (!isSyncRunning) return
    setBulkSyncIndicatorEndsAt((previous) => {
      const minEnd = Date.now() + MIN_BULK_SYNC_INDICATOR_MS
      if (previous === null || previous < minEnd) return minEnd
      return previous
    })
  }, [isSyncRunning])

  useEffect(() => {
    if (isSyncRunning || bulkSyncIndicatorEndsAt === null) return
    const remainingMs = bulkSyncIndicatorEndsAt - Date.now()
    if (remainingMs <= 0) {
      setBulkSyncIndicatorEndsAt(null)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setBulkSyncIndicatorEndsAt(null)
    }, remainingMs)
    return () => window.clearTimeout(timeoutId)
  }, [bulkSyncIndicatorEndsAt, isSyncRunning])

  // Once the visible syncing indicator ends, force a final refresh and then
  // clear optimistic local state. This avoids stale pending counts that can
  // happen when we miss a running→done transition.
  useEffect(() => {
    if (isBulkSyncIndicatorVisible) return
    if (!activeBulkMode && !optimisticPendingDelta) return

    void statusQuery.refetch().finally(() => {
      setOptimisticPendingDelta(null)
      setActiveBulkMode(null)
    })
  }, [
    activeBulkMode,
    isBulkSyncIndicatorVisible,
    optimisticPendingDelta,
    statusQuery,
  ])

  // Default the owner picker to the personal account (first owner) once loaded.
  useEffect(() => {
    if (!selectedOwner && owners.length > 0) {
      setSelectedOwner(owners[0].login)
    }
  }, [owners, selectedOwner])

  useEffect(() => {
    if (!selectedOwner || targetMode === 'create_new') {
      setSelectedExistingRepoKey('')
      return
    }
    setSelectedExistingRepoKey('')
  }, [selectedOwner, targetMode])

  useEffect(() => {
    if (targetMode === 'create_new') return
    if (selectedExistingRepoKey) return
    if (existingRepoOptions.length === 0) return
    const first = existingRepoOptions[0]
    setSelectedExistingRepoKey(normalizeRepoKey(first.ownerLogin, first.name))
  }, [existingRepoOptions, selectedExistingRepoKey, targetMode])

  useEffect(() => {
    if (!isConnectedStatus) {
      setOptimisticPendingDelta(null)
      setActiveBulkMode(null)
    }
  }, [isConnectedStatus])

  useEffect(() => {
    if (!connectedStatus || initializedConnectedEditorRef.current) return
    setSelectedOwner(
      connectedStatus.repoOwner ?? connectedStatus.githubLogin ?? '',
    )
    setTargetMode(connectedStatus.targetMode ?? 'create_new')
    setFinalizeRepoName(connectedStatus.repoName ?? DEFAULT_SYNC_REPO_NAME)
    if (connectedStatus.repoOwner && connectedStatus.repoName) {
      setSelectedExistingRepoKey(
        normalizeRepoKey(connectedStatus.repoOwner, connectedStatus.repoName),
      )
    }
    initializedConnectedEditorRef.current = true
  }, [connectedStatus])

  async function handleConnect(): Promise<void> {
    try {
      // Reset local setup state before starting OAuth so reconnect always begins
      // from a clean picker state in this session.
      setSelectedOwner('')
      setSelectedExistingRepoKey('')
      setTargetMode('create_new')
      setFinalizeRepoName(DEFAULT_SYNC_REPO_NAME)
      setInitialSyncMode('accepted_only')
      setEditingRepoTarget(false)
      initializedConnectedEditorRef.current = false
      const response = await startConnectMutation.mutateAsync()
      window.location.href = response.connectUrl
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connect failed')
    }
  }

  function parseRepoSelectionKey(key: string): RepoSelectionOption | null {
    if (!key) return null
    const [ownerLogin, name] = key.split('::')
    if (!ownerLogin || !name) return null
    return {
      ownerLogin,
      name,
    }
  }

  async function handleApplyRepositoryTarget(): Promise<void> {
    if (!selectedOwner) {
      toast.error('Select a GitHub owner')
      return
    }
    const normalizedName = normalizeRepositoryName(finalizeRepoName)
    if (targetMode === 'create_new' && !normalizedName) {
      toast.error('Enter a valid repository name')
      return
    }
    if (targetMode !== 'create_new' && !selectedExistingRepoKey) {
      toast.error('Select an existing repository')
      return
    }
    const payload = {
      ownerLogin: selectedOwner,
      targetMode,
      selectedRepo:
        targetMode === 'create_new'
          ? null
          : parseRepoSelectionKey(selectedExistingRepoKey),
      initialSyncMode,
      repoName: normalizedName || undefined,
    }
    if (isPendingSetup) {
      try {
        await finalizeMutation.mutateAsync(payload)
      } catch {
        // onError handles user-facing feedback
      }
      return
    }
    try {
      await updateConnectionTargetMutation.mutateAsync(payload)
    } catch {
      // onError handles user-facing feedback
    }
  }

  async function handleAutoSyncToggle(enabled: boolean): Promise<void> {
    setOptimisticAutoSync(enabled)
    try {
      await updateAutoSyncMutation.mutateAsync({
        autoSyncEnabled: enabled,
        autoSyncMode: uiMode,
      })
    } catch {
      // onError handles user-facing feedback
    } finally {
      setOptimisticAutoSync(null)
    }
  }

  async function handleAutoSyncModeChange(mode: SyncMode): Promise<void> {
    try {
      await updateAutoSyncMutation.mutateAsync({
        autoSyncEnabled: connectedStatus?.autoSyncEnabled ?? true,
        autoSyncMode: mode,
      })
    } catch {
      // onError handles user-facing feedback
    }
  }

  async function handleRenameRepository(): Promise<void> {
    const normalizedName = normalizeRepositoryName(renameRepoName)
    if (!normalizedName) {
      toast.error('Enter a valid repository name')
      return
    }
    try {
      await renameRepositoryMutation.mutateAsync({ newName: normalizedName })
      setRenameRepoName('')
    } catch {
      // onError handles user-facing feedback
    }
  }

  function beginRepoTargetEditing(): void {
    if (!connectedStatus) return
    setEditingRepoTarget(true)
    setSelectedOwner(
      connectedStatus.repoOwner ?? connectedStatus.githubLogin ?? '',
    )
    setTargetMode(connectedStatus.targetMode ?? 'create_new')
    setFinalizeRepoName(connectedStatus.repoName ?? DEFAULT_SYNC_REPO_NAME)
    if (connectedStatus.repoOwner && connectedStatus.repoName) {
      setSelectedExistingRepoKey(
        normalizeRepoKey(connectedStatus.repoOwner, connectedStatus.repoName),
      )
    } else {
      setSelectedExistingRepoKey('')
    }
    setInitialSyncMode('accepted_only')
  }

  function cancelRepoTargetEditing(): void {
    setEditingRepoTarget(false)
  }

  return (
    <div>
      <div className="border-b pb-4">
        <h1 className="text-xl font-semibold">GitHub Integration</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Automatically sync your solutions to a GitHub repository you choose.
        </p>
      </div>

      {isSetupEditorVisible ? (
        <div className="mt-6 space-y-4">
          {!isPendingSetup ? (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelRepoTargetEditing}
                disabled={isBusy}
              >
                Go back
              </Button>
            </div>
          ) : null}
          <div>
            <p className="text-sm text-foreground">
              {isPendingSetup
                ? 'Authorized as'
                : 'Editing repository target for'}{' '}
              <span className="font-medium">{status?.githubLogin}</span>. Choose
              how you want to connect your public sync target.
            </p>
          </div>

          <div className="mt-4 space-y-3">
            <SettingsSection title="Repo Location">
              <SettingRow
                stacked
                label="Repository owner"
                description="Your personal account or an organization you belong to."
              >
                {ownersQuery.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <NativeSelect
                    className="w-72"
                    value={selectedOwner}
                    onChange={(value) => setSelectedOwner(value)}
                    options={
                      owners.length > 0
                        ? owners.map((owner) => ({
                            value: owner.login,
                            label:
                              owner.type === 'user'
                                ? `${owner.login} (personal)`
                                : `${owner.login} (org)`,
                          }))
                        : [
                            {
                              value: '',
                              label: 'No owners available',
                              disabled: true,
                            },
                          ]
                    }
                    disabled={isBusy || owners.length === 0}
                  />
                )}
              </SettingRow>
            </SettingsSection>

            <SettingsSection title="Repo Type">
              <SettingRow
                stacked
                label="Target repository option"
                description="Choose where BeyondCTCI should sync your submissions."
              >
                <div className="grid gap-2" role="radiogroup">
                  {TARGET_MODE_OPTIONS.map((option) => (
                    <SelectionCard
                      key={option.value}
                      title={option.label}
                      description={
                        option.value === 'create_new'
                          ? 'Create a brand-new public repository managed by BeyondCTCI.'
                          : option.value === 'use_existing_bctci'
                            ? 'Reconnect a repository already managed by BeyondCTCI.'
                            : 'External repository mode writes into a fixed top-level /beyondctci-code-submissions directory and disables repository renaming.'
                      }
                      selected={targetMode === option.value}
                      onClick={() => setTargetMode(option.value)}
                    />
                  ))}
                </div>
              </SettingRow>

              {targetMode === 'create_new' ? (
                <SettingRow
                  stacked
                  label="Repository name"
                  description="A new public repository will be created with this name."
                >
                  <input
                    type="text"
                    value={finalizeRepoName}
                    onChange={(event) =>
                      setFinalizeRepoName(event.target.value)
                    }
                    placeholder={DEFAULT_SYNC_REPO_NAME}
                    className="h-8 w-72 rounded-md border border-border bg-background px-2 text-sm"
                    disabled={isBusy}
                  />
                </SettingRow>
              ) : null}

              {targetMode !== 'create_new' ? (
                <SettingRow
                  stacked
                  label="Existing repository"
                  description={
                    targetMode === 'use_existing_bctci'
                      ? 'Only repositories recognized as BeyondCTCI-managed are shown.'
                      : 'Only public repositories not currently managed by BeyondCTCI are shown.'
                  }
                >
                  {ownerReposQuery.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <NativeSelect
                      className="w-full max-w-[34rem]"
                      value={selectedExistingRepoKey}
                      onChange={(value) => setSelectedExistingRepoKey(value)}
                      options={
                        existingRepoOptions.length > 0
                          ? existingRepoOptions.map((repo) => ({
                              value: normalizeRepoKey(
                                repo.ownerLogin,
                                repo.name,
                              ),
                              label: repo.fullName,
                            }))
                          : [
                              {
                                value: '',
                                label: 'No repositories found for this owner',
                                disabled: true,
                              },
                            ]
                      }
                      disabled={isBusy || existingRepoOptions.length === 0}
                    />
                  )}
                </SettingRow>
              ) : null}
            </SettingsSection>

            <SettingsSection title="Repo Contents">
              <SettingRow
                stacked
                label="Initial sync mode"
                description="Choose what to sync to your target repository."
              >
                <div className="grid gap-2" role="radiogroup">
                  {INITIAL_BACKFILL_CARDS.map((option) => (
                    <SelectionCard
                      key={option.value}
                      title={option.title}
                      description={option.description}
                      selected={initialSyncMode === option.value}
                      onClick={() =>
                        setInitialSyncMode(option.value as InitialBackfillMode)
                      }
                    />
                  ))}
                </div>
              </SettingRow>
            </SettingsSection>
          </div>

          {ownersQuery.isError ? (
            <p className="text-destructive text-xs">
              {ownersQuery.error.message}
            </p>
          ) : null}
          {ownerReposQuery.isError ? (
            <p className="text-destructive text-xs">
              {ownerReposQuery.error.message}
            </p>
          ) : null}
          {!ownerReposQuery.isLoading &&
          targetMode !== 'create_new' &&
          selectedOwner &&
          existingRepoOptions.length === 0 ? (
            <SettingRow
              stacked
              label="No repositories found for this option"
              description={
                targetMode === 'use_existing_bctci'
                  ? 'No recognized BeyondCTCI-managed public repositories were found for this owner yet.'
                  : 'No eligible public external repositories were found for this owner.'
              }
            >
              <span className="text-muted-foreground text-xs">
                Try a different owner or target option.
              </span>
            </SettingRow>
          ) : null}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => void handleApplyRepositoryTarget()}
              disabled={
                isBusy ||
                !selectedOwner ||
                (targetMode === 'create_new'
                  ? !normalizeRepositoryName(finalizeRepoName)
                  : !selectedExistingRepoKey)
              }
            >
              {finalizeMutation.isPending ||
              updateConnectionTargetMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Sync repository
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                isPendingSetup
                  ? setDisconnectOpen(true)
                  : cancelRepoTargetEditing()
              }
              disabled={isBusy}
            >
              {isPendingSetup ? 'Cancel' : 'Go back'}
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Don&apos;t see your organization? Org owners may need to approve
            BeyondCTCI, and orgs with private membership require granting the
            organization-read permission during authorization.
          </p>
        </div>
      ) : !isConnectedStatus ? (
        <div className="mt-6 space-y-3">
          {!configReady ? (
            <p className="text-destructive text-sm">
              GitHub integration isn&apos;t configured yet. An administrator
              needs to set the GitHub OAuth credentials before you can connect.
            </p>
          ) : null}
          <Button
            type="button"
            onClick={() => setConnectConfirmOpen(true)}
            disabled={startConnectMutation.isPending || !configReady}
          >
            {startConnectMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Sync your GitHub account
          </Button>
          <p className="text-muted-foreground text-xs">
            v1 supports one connected GitHub account and public repositories
            only.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <SettingsSection title="Setup Repo">
            <SettingRow
              stacked
              label="Connected account"
              description={
                <span className="inline-flex items-center gap-2">
                  {connectedOwnerAvatar ? (
                    <img
                      src={connectedOwnerAvatar}
                      alt="GitHub avatar"
                      className="h-7 w-7 rounded-full object-cover"
                    />
                  ) : (
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                      {connectedStatus?.githubLogin?.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span>{connectedStatus?.githubLogin}</span>
                  <span className="rounded-full border border-emerald-400/60 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                    Connected
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <span className="max-w-[22rem] truncate">
                    {connectedStatus?.repoFullName}
                  </span>
                </span>
              }
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={beginRepoTargetEditing}
                disabled={isBusy}
              >
                Manage Repo
              </Button>
            </SettingRow>

            {connectedStatus?.targetMode !== 'use_existing_external' ? (
              <SettingRow
                stacked
                label="Rename repository"
                description="Rename on GitHub while keeping existing history."
              >
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={renameRepoName}
                    onChange={(event) => setRenameRepoName(event.target.value)}
                    placeholder={connectedStatus?.repoName ?? 'Repository name'}
                    className="h-8 w-56 rounded-md border border-border bg-background px-2 text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!renameRepoName.trim() || isBusy}
                    onClick={() => void handleRenameRepository()}
                  >
                    Rename
                  </Button>
                </div>
              </SettingRow>
            ) : null}
          </SettingsSection>

          <SettingsSection title="Commit Settings">
            <SettingRow
              label="Auto-commit on submission"
              description="Enable automatic one-way sync after each submission."
            >
              <Toggle
                checked={
                  optimisticAutoSync ?? connectedStatus?.autoSyncEnabled ?? true
                }
                onChange={(enabled) => void handleAutoSyncToggle(enabled)}
                aria-label="Toggle automatic GitHub sync"
                disabled={isBusy}
              />
            </SettingRow>

            <SettingRow label="Auto-sync submission type">
              <NativeSelect
                className="w-[17ch] min-w-[17ch]"
                value={uiMode}
                onChange={(value) =>
                  void handleAutoSyncModeChange(value as SyncMode)
                }
                options={SYNC_MODE_OPTIONS}
                disabled={isBusy || !connectedStatus?.autoSyncEnabled}
              />
            </SettingRow>
          </SettingsSection>

          <SettingsSection title="Bulk Sync">
            <SettingRow
              label="Sync previous submissions"
              description={
                <span className="inline-flex items-center gap-1">
                  <span className="inline-flex items-center gap-1">
                    Accepted only pending: {displayedPendingAccepted}
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    All submissions pending: {displayedPendingAll}
                  </span>
                </span>
              }
            >
              <div className="flex items-center gap-2">
                <NativeSelect
                  className="w-52"
                  value={bulkMode}
                  onChange={(value) => setBulkMode(value as SyncMode)}
                  options={SYNC_MODE_OPTIONS}
                  disabled={isBusy}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setBulkSyncIndicatorEndsAt(
                      Date.now() + MIN_BULK_SYNC_INDICATOR_MS,
                    )
                    triggerBulkSyncMutation.mutate({ mode: bulkMode })
                  }}
                  disabled={
                    isBusy ||
                    isBulkSyncIndicatorVisible ||
                    (bulkMode === 'accepted_only'
                      ? displayedPendingAccepted === 0
                      : displayedPendingAll === 0)
                  }
                >
                  {isBulkSyncIndicatorVisible ? (
                    <>
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-label={
                          spinnerMode === 'all_submissions'
                            ? 'All-submissions sync in progress'
                            : 'Accepted-only sync in progress'
                        }
                      />
                      Syncing
                    </>
                  ) : (
                    'Sync all now'
                  )}
                </Button>
              </div>
            </SettingRow>
            {isBulkSyncIndicatorVisible ? (
              <div className="text-muted-foreground flex items-center gap-2 px-0 py-2 text-sm">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                <span>
                  Syncing{' '}
                  {spinnerMode === 'all_submissions'
                    ? 'all submissions'
                    : 'accepted submissions'}
                  ...
                </span>
              </div>
            ) : null}
          </SettingsSection>

          <SettingsSection
            title="Danger Zone"
            titleClassName="text-destructive"
            danger
          >
            <SettingRow
              label="Disconnect GitHub"
              description={`Status: ${connectedStatus?.syncStatus} • Last synced: ${
                connectedStatus?.lastSyncedAt
                  ? new Date(connectedStatus.lastSyncedAt).toLocaleString()
                  : 'Never'
              }${
                connectedStatus?.lastError
                  ? ` • Last error: ${connectedStatus.lastError}`
                  : ''
              }`}
            >
              <Button
                type="button"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                size="sm"
                onClick={() => setDisconnectOpen(true)}
                disabled={isBusy}
              >
                Disconnect GitHub
              </Button>
            </SettingRow>
          </SettingsSection>
        </div>
      )}

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent
          className="z-[100000]"
          overlayClassName="z-[100000]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect GitHub account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke GitHub permissions and stop sync activity for
              this account. You can reconnect later and select any eligible
              repository again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDisconnectOpen(false)
                disconnectMutation.mutate()
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={connectConfirmOpen}
        onOpenChange={setConnectConfirmOpen}
      >
        <AlertDialogContent
          className="z-[100000]"
          overlayClassName="z-[100000]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Before continuing to GitHub</AlertDialogTitle>
            <AlertDialogDescription>
              We request the minimum required permissions to:
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Read who owns the selected account or organization to
                  determine placement for the repository BeyondCTCI will sync
                  to.
                </li>
                <li>
                  Perform one-way pushes from BeyondCTCI to your designated
                  repository.
                </li>
              </ul>
              <div className="mt-3 space-y-2 text-xs">
                <p>
                  See permissions (
                  <Tooltip.Provider delayDuration={120}>
                    <PermissionScopeTooltip
                      scope="public_repo"
                      description="Allows creating and updating files in public repositories you choose for sync."
                    />
                  </Tooltip.Provider>
                  ,{' '}
                  <Tooltip.Provider delayDuration={120}>
                    <PermissionScopeTooltip
                      scope="read:org"
                      description="Allows you to pick what organization you're looking to sync to, including your personal org."
                    />
                  </Tooltip.Provider>
                  )
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConnectConfirmOpen(false)
                void handleConnect()
              }}
            >
              Continue to GitHub
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
